import React, { useEffect, useRef, useState } from 'react';
import {
  Key, RefreshCw, Eye, EyeOff, Copy, Check, Webhook,
  CircleAlert, CircleCheck, ChevronRight, ExternalLink,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { merchantCredentialsAPI } from '../../services/api';

// ─── Design tokens ──────────────────────────────────────────────────────────
const C = {
  bg:        '#f4f4f9',
  bgSub:     '#f9f9fc',
  border:    '#e8e8f1',
  borderSoft:'#f0f0f6',
  text:      '#14142b',
  textSub:   '#4a4a6a',
  textMuted: '#6e6e86',
  accent:    '#7c3aed',
  accentHov: '#6d28d9',
  green:     '#10b981',
  greenBg:   '#d1fae5',
  greenBorder:'#a7f3d0',
  red:       '#ef4444',
  redBg:     '#fee2e2',
  redBorder: '#fecaca',
  yellow:    '#f59e0b',
  yellowBg:  '#fef3c7',
  yellowBorder:'#fde68a',
  blue:      '#7c3aed',
  blueBg:    '#f1ebff',
  blueBorder:'#ddd6fe',
  codebg: '#f4f4f9',
  codeDark:  '#14142b',
};

// ─── Shared primitives ───────────────────────────────────────────────────────

const useClipboard = () => {
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  return { copy, copied };
};

const CopyButton = ({ text, id, copied, copy, style = {} }) => (
  <button
    onClick={() => copy(text, id)}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
      background: copied === id ? C.greenBg : C.bgSub,
      color: copied === id ? C.green : C.textSub,
      fontSize: 12, fontWeight: 500, cursor: 'pointer',
      transition: 'all .12s', ...style,
    }}
  >
    {copied === id ? <Check size={12} /> : <Copy size={12} />}
    {copied === id ? 'Copied' : 'Copy'}
  </button>
);

const CodeBlock = ({ code, lang, copied, copy }) => {
  const id = lang + code.slice(0, 20);
  return (
    <div style={{ position: 'relative', marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px',
        background: '#1e293b', borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid rgba(255,255,255,.08)',
      }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>
          {lang}
        </span>
        <CopyButton
          text={code} id={id} copied={copied} copy={copy}
          style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.12)', color: '#6e6e86' }}
        />
      </div>
      <pre style={{
        margin: 0, padding: '16px 18px',
        background: C.codeDark, borderRadius: '0 0 8px 8px',
        fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
        fontSize: 13, lineHeight: 1.7, color: '#e8e8f1',
        overflowX: 'auto', whiteSpace: 'pre',
      }}>{code}</pre>
    </div>
  );
};

const InlineCode = ({ children }) => (
  <code style={{
    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
    fontSize: '0.875em', background: C.codeBg,
    border: `1px solid ${C.border}`,
    borderRadius: 4, padding: '1px 6px', color: C.text,
  }}>{children}</code>
);

const Callout = ({ icon: Icon, title, children, variant = 'info' }) => {
  const v = {
    info:    { bg: C.blueBg,   border: C.blueBorder,   text: C.blue,   iconC: C.blue   },
    success: { bg: C.greenBg,  border: C.greenBorder,  text: C.green,  iconC: C.green  },
    warning: { bg: C.yellowBg, border: C.yellowBorder, text: C.yellow, iconC: C.yellow },
    danger:  { bg: C.redBg,    border: C.redBorder,    text: C.red,    iconC: C.red    },
  }[variant];
  return (
    <div style={{
      background: v.bg, border: `1px solid ${v.border}`,
      borderRadius: 8, padding: '12px 16px', marginBottom: 20,
      display: 'flex', gap: 10,
    }}>
      {Icon && <Icon size={16} style={{ color: v.iconC, flexShrink: 0, marginTop: 1 }} />}
      <div>
        {title && <div style={{ fontWeight: 600, fontSize: 13, color: v.text, marginBottom: 3 }}>{title}</div>}
        <div style={{ fontSize: 13, color: v.text, lineHeight: 1.65 }}>{children}</div>
      </div>
    </div>
  );
};

const FieldRow = ({ label, hint, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 }}>{label}</div>
    {children}
    {hint && <div style={{ fontSize: 12, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>{hint}</div>}
  </div>
);

const InputRow = ({ value, type = 'text', onCopy, copyLabel, extra, placeholder }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 0,
    border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden',
    background: C.bgSub,
  }}>
    <input
      readOnly type={type} value={value} placeholder={placeholder}
      style={{
        flex: 1, padding: '9px 12px', border: 'none', background: 'transparent',
        fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
        fontSize: 13, color: C.text, outline: 'none',
      }}
    />
    {onCopy && (
      <button
        onClick={onCopy}
        style={{
          padding: '0 12px', background: 'none', border: 'none',
          borderLeft: `1px solid ${C.border}`, cursor: 'pointer',
          color: C.textSub, display: 'flex', alignItems: 'center', height: '100%',
        }}
        title="Copy"
      >
        <Copy size={14} />
      </button>
    )}
    {extra}
  </div>
);

const ActionBtn = ({ onClick, disabled, loading, children, variant = 'primary' }) => {
  const styles = {
    primary:   { bg: C.text,    color: '#fff',      border: C.text    },
    secondary: { bg: C.bgSub,   color: C.text,      border: C.border  },
    danger:    { bg: C.redBg,   color: C.red,       border: C.redBorder },
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 16px', borderRadius: 7,
        border: `1px solid ${styles.border}`,
        background: disabled ? C.bgSub : styles.bg,
        color: disabled ? C.textMuted : styles.color,
        fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all .12s',
      }}
    >{loading ? '...' : children}</button>
  );
};

const Divider = () => <hr style={{ border: 'none', borderTop: `1px solid ${C.borderSoft}`, margin: '32px 0' }} />;

const SectionTitle = ({ id, children, refEl }) => (
  <h2
    id={id}
    ref={refEl}
    style={{
      fontSize: 20, fontWeight: 700, color: C.text,
      margin: '0 0 6px', paddingTop: 4,
      scrollMarginTop: 80,
    }}
  >{children}</h2>
);

const SectionSub = ({ children }) => (
  <p style={{ fontSize: 14, color: C.textSub, margin: '0 0 20px', lineHeight: 1.6 }}>{children}</p>
);

// ─── Code samples ──────────────────────────────────────────────────────────
const CODE = {
  nodeCreate: `const crypto = require('crypto');
const axios  = require('axios');

const API_KEY     = process.env.A1XPAY_API_KEY;
const SECRET_SALT = process.env.A1XPAY_SECRET_SALT;
const MERCHANT_ID = process.env.A1XPAY_MERCHANT_ID;

async function createOrder(amount, orderId, redirectUrl, webhookUrl) {
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const canonical = \`\${amount}|\${orderId}|\${timestamp}|\${MERCHANT_ID}\`;
  const signature = crypto
    .createHmac('sha256', SECRET_SALT)
    .update(canonical)
    .digest('hex');

  const { data } = await axios.post(
    'https://api.a1xpay.com/api/v1/create-order',
    { amount, merchant_order_id: orderId, redirect_url: redirectUrl, webhook_url: webhookUrl },
    {
      headers: {
        'Content-Type': 'application/json',
        'X-Api-Key':   API_KEY,
        'X-Signature': signature,
        'X-Timestamp': timestamp,
      },
    }
  );
  // data → { success: true, txn_id: "TXN_...", checkout_url: "https://..." }
  return data;
}`,

  phpCreate: `<?php
$apiKey     = getenv('A1XPAY_API_KEY');
$secretSalt = getenv('A1XPAY_SECRET_SALT');
$merchantId = getenv('A1XPAY_MERCHANT_ID');

$amount    = '500.00';
$orderId   = 'ORD_' . time();
$timestamp = (string) time();

$canonical = "$amount|$orderId|$timestamp|$merchantId";
$signature = hash_hmac('sha256', $canonical, $secretSalt);

$response = json_decode(file_get_contents(
  'https://api.a1xpay.com/api/v1/create-order',
  false,
  stream_context_create([
    'http' => [
      'method'  => 'POST',
      'header'  => implode("\\r\\n", [
        'Content-Type: application/json',
        "X-Api-Key: $apiKey",
        "X-Signature: $signature",
        "X-Timestamp: $timestamp",
      ]),
      'content' => json_encode([
        'amount'            => $amount,
        'merchant_order_id' => $orderId,
        'redirect_url'      => 'https://yoursite.com/return',
        'webhook_url'       => 'https://yoursite.com/webhook',
      ]),
    ],
  ])
), true);

// $response['checkout_url'] → redirect customer here`,

  nodeWebhook: `const express = require('express');
const crypto  = require('crypto');
const app     = express();

app.post('/webhook/a1xpay', express.json(), (req, res) => {
  const sig       = req.headers['x-signature'];
  const timestamp = req.headers['x-timestamp'];
  const { txn_id, status, amount, merchant_order_id } = req.body;

  const canonical = \`\${txn_id}|\${status}|\${amount}|\${merchant_order_id}|\${timestamp}\`;
  const expected  = crypto
    .createHmac('sha256', process.env.A1XPAY_SECRET_SALT)
    .update(canonical)
    .digest('hex');

  if (!crypto.timingSafeEqual(Buffer.from(sig, 'hex'), Buffer.from(expected, 'hex'))) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  if (status === 'success') {
    // ✅ Payment confirmed — fulfill the order
    console.log('Order paid:', merchant_order_id, amount);
  }

  res.json({ received: true });
});`,

  phpWebhook: `<?php
$payload   = file_get_contents('php://input');
$sig       = $_SERVER['HTTP_X_SIGNATURE'] ?? '';
$timestamp = $_SERVER['HTTP_X_TIMESTAMP']  ?? '';

$data      = json_decode($payload, true);
$canonical = "{$data['txn_id']}|{$data['status']}|{$data['amount']}"
           . "|{$data['merchant_order_id']}|{$timestamp}";

$expected  = hash_hmac('sha256', $canonical, getenv('A1XPAY_SECRET_SALT'));

if (!hash_equals($expected, $sig)) {
    http_response_code(401);
    exit('Invalid signature');
}

if ($data['status'] === 'success') {
    // ✅ Payment confirmed
}

echo json_encode(['received' => true]);`,
};

// ─── Nav sections ──────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'credentials', label: 'Credentials'     },
  { id: 'webhook-url', label: 'Webhook URL'      },
  { id: 'auth',        label: 'Authentication'   },
  { id: 'create',      label: 'Create Order'     },
  { id: 'webhooks',    label: 'Webhooks'         },
  { id: 'status',      label: 'Status Codes'     },
];

// ─── Main component ────────────────────────────────────────────────────────
export default function UserApiIntegration() {
  const [creds,        setCreds]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [secretSalt,   setSecretSalt]   = useState(null);
  const [revealedOnce, setRevealedOnce] = useState(false);
  const [showSalt,     setShowSalt]     = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [webhookUrl,   setWebhookUrl]   = useState('');
  const [savingWh,     setSavingWh]     = useState(false);
  const [langCreate,   setLangCreate]   = useState('node');
  const [langWebhook,  setLangWebhook]  = useState('node');
  const [activeNav,    setActiveNav]    = useState('credentials');
  const { copy, copied } = useClipboard();
  const sectionRefs = useRef({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await merchantCredentialsAPI.show();
      setCreds(data?.data || null);
      setWebhookUrl(data?.data?.default_webhook_url || '');
    } catch { toast.error('Could not load credentials'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => { if (e.isIntersecting) setActiveNav(e.target.id); });
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [loading]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveNav(id);
  };

  const handleReveal = async () => {
    try {
      const { data } = await merchantCredentialsAPI.revealOnce();
      setSecretSalt(data?.data?.secret_salt);
      setRevealedOnce(true);
      setShowSalt(true);
      if (data?.data?.api_key) {
        setCreds(prev => ({ ...prev, api_key: data.data.api_key }));
      }
      toast.warn('Save this salt now — it will never be shown again.');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Salt not available. Contact support to rotate.');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate API key? The current key will stop working immediately.')) return;
    setRegenerating(true);
    try {
      await merchantCredentialsAPI.regenerate();
      toast.success('API key regenerated.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not regenerate.');
    } finally { setRegenerating(false); }
  };

  const saveWebhook = async () => {
    setSavingWh(true);
    try {
      await merchantCredentialsAPI.updateWebhookUrl(webhookUrl || null);
      toast.success('Webhook URL saved.');
    } catch (e) {
      toast.error(e?.response?.data?.errors?.webhook_url?.[0] || 'Save failed.');
    } finally { setSavingWh(false); }
  };

  const LangTabs = ({ lang, setLang, options }) => (
    <div style={{ display: 'flex', gap: 2, marginBottom: 8 }}>
      {options.map(o => (
        <button
          key={o.value}
          onClick={() => setLang(o.value)}
          style={{
            padding: '5px 14px', borderRadius: 6, border: `1px solid ${C.border}`,
            background: lang === o.value ? C.text : C.bgSub,
            color: lang === o.value ? '#fff' : C.textSub,
            fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}
        >{o.label}</button>
      ))}
    </div>
  );

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80, color: C.textMuted, fontSize: 14 }}>
      Loading…
    </div>
  );

  const isActive = creds?.account_status === 'active';

  return (
    <div className="doc-layout">
      <style>{`
        .doc-layout { max-width: 1100px; margin: 0 auto; display: flex; gap: 48px; align-items: flex-start; }
        .doc-toc { width: 200px; flex-shrink: 0; }
        @media (max-width: 768px) { .doc-toc { display: none; } }
      `}</style>

      {/* ── Left sticky nav ── */}
      <nav className="doc-toc" style={{ position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>
          On this page
        </div>
        {SECTIONS.map(s => (
          <button
            key={s.id}
            onClick={() => scrollTo(s.id)}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 10px', borderRadius: 6, border: 'none',
              background: activeNav === s.id ? C.bgSub : 'transparent',
              color: activeNav === s.id ? C.text : C.textSub,
              fontWeight: activeNav === s.id ? 600 : 400,
              fontSize: 13, cursor: 'pointer', marginBottom: 1,
              borderLeft: `2px solid ${activeNav === s.id ? C.text : 'transparent'}`,
              transition: 'all .1s',
            }}
          >{s.label}</button>
        ))}
      </nav>

      {/* ── Main content ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Page header */}
        <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6 }}>
            Manage Integrations
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>
            API Setup
          </h1>
          <p style={{ fontSize: 15, color: C.textSub, margin: 0, lineHeight: 1.6 }}>
            Integrate A1xPay into your server using signed API requests.
            Every call is authenticated with HMAC-SHA256.
          </p>

          {/* Status badge */}
          <div style={{ marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 14px', borderRadius: 8, border: `1px solid ${isActive ? C.greenBorder : C.redBorder}`, background: isActive ? C.greenBg : C.redBg }}>
            {isActive
              ? <CircleCheck size={14} style={{ color: C.green }} />
              : <CircleAlert size={14} style={{ color: C.red }} />}
            <span style={{ fontSize: 13, fontWeight: 600, color: isActive ? C.green : C.red }}>
              Account {creds?.account_status || 'inactive'}
            </span>
          </div>
        </div>

        {/* ── Credentials ── */}
        <section>
          <SectionTitle id="credentials" refEl={el => sectionRefs.current['credentials'] = el}>
            Credentials
          </SectionTitle>
          <SectionSub>Store these as environment variables on your server. Never expose them in frontend code.</SectionSub>

          <FieldRow label="Merchant ID">
            <InputRow
              value={creds?.merchant_id || ''}
              onCopy={() => copy(creds?.merchant_id, 'mid')}
            />
          </FieldRow>

          <FieldRow
            label="API Key"
            hint={creds?.api_key_issued_at
              ? `Issued ${new Date(creds.api_key_issued_at).toLocaleDateString()}. Regenerating immediately invalidates the current key.`
              : 'Not yet issued.'}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <InputRow
                value={creds?.api_key || '— not yet issued —'}
                onCopy={() => copy(creds?.api_key, 'apikey')}
              />
              <ActionBtn
                onClick={handleRegenerate}
                loading={regenerating}
                disabled={regenerating}
                variant="secondary"
              >
                <RefreshCw size={13} /> Regenerate
              </ActionBtn>
            </div>
          </FieldRow>

          <FieldRow
            label="Secret Salt"
            hint="Used only for HMAC signing on your server. Never sent in requests. Shown once — save it to your secret manager."
          >
            {!revealedOnce ? (
              <ActionBtn onClick={handleReveal}>
                <Eye size={13} /> Reveal secret salt
              </ActionBtn>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <InputRow
                  value={secretSalt || ''}
                  type={showSalt ? 'text' : 'password'}
                  onCopy={() => copy(secretSalt, 'salt')}
                  extra={
                    <button
                      onClick={() => setShowSalt(s => !s)}
                      style={{
                        padding: '0 12px', background: 'none', border: 'none',
                        borderLeft: `1px solid ${C.border}`, cursor: 'pointer',
                        color: C.textSub, display: 'flex', alignItems: 'center',
                      }}
                    >
                      {showSalt ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  }
                />
              </div>
            )}
          </FieldRow>
        </section>

        <Divider />

        {/* ── Webhook URL ── */}
        <section>
          <SectionTitle id="webhook-url" refEl={el => sectionRefs.current['webhook-url'] = el}>
            Webhook URL
          </SectionTitle>
          <SectionSub>
            Default URL where A1xPay POSTs payment results. A per-request <InlineCode>webhook_url</InlineCode> in <InlineCode>/create-order</InlineCode> overrides this.
          </SectionSub>

          <FieldRow label="Default Webhook URL">
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                placeholder="https://yoursite.com/webhook/a1xpay"
                value={webhookUrl}
                onChange={e => setWebhookUrl(e.target.value)}
                style={{
                  flex: 1, padding: '9px 12px', border: `1px solid ${C.border}`,
                  borderRadius: 8, fontSize: 13, outline: 'none',
                  background: C.bgSub, color: C.text,
                }}
              />
              <ActionBtn onClick={saveWebhook} loading={savingWh} disabled={savingWh}>
                <Webhook size={13} /> Save
              </ActionBtn>
            </div>
          </FieldRow>
        </section>

        <Divider />

        {/* ── Authentication ── */}
        <section>
          <SectionTitle id="auth" refEl={el => sectionRefs.current['auth'] = el}>
            Authentication
          </SectionTitle>
          <SectionSub>
            Every request must include three headers. The signature is computed server-side using your secret salt.
          </SectionSub>

          <div style={{ overflowX: 'auto', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bgSub }}>
                  {['Header', 'Description'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['X-Api-Key',   'Your API key.'],
                  ['X-Timestamp', 'Current Unix timestamp in seconds. Rejected if more than 5 minutes off.'],
                  ['X-Signature', 'HMAC-SHA256 hex of amount|merchant_order_id|timestamp|merchant_id using your secret salt.'],
                ].map(([h, d], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '10px 14px', verticalAlign: 'top' }}><InlineCode>{h}</InlineCode></td>
                    <td style={{ padding: '10px 14px', color: C.textSub }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 13, color: C.textSub, margin: '0 0 8px' }}>
            <strong style={{ color: C.text }}>Signature canonical string:</strong>
          </p>
          <CodeBlock code={'amount|merchant_order_id|timestamp|merchant_id'} lang="format" copied={copied} copy={copy} />
        </section>

        <Divider />

        {/* ── Create Order ── */}
        <section>
          <SectionTitle id="create" refEl={el => sectionRefs.current['create'] = el}>
            Create Order
          </SectionTitle>
          <SectionSub>
            Initiate a payment. Returns a <InlineCode>checkout_url</InlineCode> to redirect the customer.
          </SectionSub>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20,
            padding: '6px 14px', borderRadius: 8,
            border: `1px solid ${C.border}`, background: C.bgSub,
            fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace', fontSize: 13,
          }}>
            <span style={{ fontWeight: 700, color: '#16a34a' }}>POST</span>
            <span style={{ color: C.text }}>/api/v1/create-order</span>
          </div>

          <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>Request body</p>
          <div style={{ overflowX: 'auto', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bgSub }}>
                  {['Field', 'Type', 'Required', 'Description'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['amount',            'string', 'Yes', 'Payment amount in INR, e.g. "500.00".'],
                  ['merchant_order_id', 'string', 'Yes', 'Your unique order reference. Used in webhook and redirect.'],
                  ['redirect_url',      'string', 'Yes', 'URL to send customer after payment.'],
                  ['webhook_url',       'string', 'No',  'Override default webhook URL for this order.'],
                ].map(([f, t, r, d], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '10px 14px' }}><InlineCode>{f}</InlineCode></td>
                    <td style={{ padding: '10px 14px', color: C.textMuted }}>{t}</td>
                    <td style={{ padding: '10px 14px', color: r === 'Yes' ? C.red : C.textMuted, fontWeight: r === 'Yes' ? 600 : 400 }}>{r}</td>
                    <td style={{ padding: '10px 14px', color: C.textSub }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <LangTabs lang={langCreate} setLang={setLangCreate} options={[{ value: 'node', label: 'Node.js' }, { value: 'php', label: 'PHP' }]} />
          <CodeBlock code={langCreate === 'node' ? CODE.nodeCreate : CODE.phpCreate} lang={langCreate === 'node' ? 'javascript' : 'php'} copied={copied} copy={copy} />

          <Callout icon={ChevronRight} title="Response">
            <InlineCode>{'{ "success": true, "txn_id": "TXN_...", "checkout_url": "https://..." }'}</InlineCode>
            <span style={{ display: 'block', marginTop: 6 }}>Redirect the customer's browser to <InlineCode>checkout_url</InlineCode>.</span>
          </Callout>
        </section>

        <Divider />

        {/* ── Webhooks ── */}
        <section>
          <SectionTitle id="webhooks" refEl={el => sectionRefs.current['webhooks'] = el}>
            Webhooks
          </SectionTitle>
          <SectionSub>
            A1xPay POSTs payment results to your webhook URL. Verify the <InlineCode>X-Signature</InlineCode> header before processing.
          </SectionSub>

          <p style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>Payload fields</p>
          <div style={{ overflowX: 'auto', marginBottom: 20 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bgSub }}>
                  {['Field', 'Description'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['txn_id',            'A1xPay transaction ID.'],
                  ['status',            '"success" or "failed".'],
                  ['amount',            'Paid amount.'],
                  ['merchant_order_id', 'Your order reference from create-order.'],
                ].map(([f, d], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '10px 14px' }}><InlineCode>{f}</InlineCode></td>
                    <td style={{ padding: '10px 14px', color: C.textSub }}>{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Callout icon={CircleAlert} title="Always verify the signature" variant="warning">
            Compute HMAC-SHA256 of <InlineCode>txn_id|status|amount|merchant_order_id|timestamp</InlineCode> using your secret salt and compare with <InlineCode>X-Signature</InlineCode>.
          </Callout>

          <LangTabs lang={langWebhook} setLang={setLangWebhook} options={[{ value: 'node', label: 'Node.js' }, { value: 'php', label: 'PHP' }]} />
          <CodeBlock code={langWebhook === 'node' ? CODE.nodeWebhook : CODE.phpWebhook} lang={langWebhook === 'node' ? 'javascript' : 'php'} copied={copied} copy={copy} />
        </section>

        <Divider />

        {/* ── Status Codes ── */}
        <section>
          <SectionTitle id="status" refEl={el => sectionRefs.current['status'] = el}>
            Status Codes
          </SectionTitle>
          <SectionSub>HTTP status codes returned by the API.</SectionSub>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: C.bgSub }}>
                  {['Code', 'Meaning'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: C.text, borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['200 / 201', 'Success.'],
                  ['401',       'Missing or invalid API key / signature.'],
                  ['403',       'Merchant account inactive.'],
                  ['422',       'Validation error — check the errors field.'],
                  ['429',       'Rate limited — slow down requests.'],
                  ['500',       'Server error — retry with exponential backoff.'],
                ].map(([code, meaning], i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${C.borderSoft}` }}>
                    <td style={{ padding: '10px 14px' }}><InlineCode>{code}</InlineCode></td>
                    <td style={{ padding: '10px 14px', color: C.textSub }}>{meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Bottom padding */}
        <div style={{ height: 64 }} />
      </div>
    </div>
  );
}
